import { useEffect, useState } from 'react';
import { tosPath, privacyPolicyPath } from '@paths';
import { LinkBracket } from '@components';

const prevenetDefaultOnEnter: React.KeyboardEventHandler<HTMLInputElement> = (
  event
) => {
  if (event.key === 'Enter') {
    event.preventDefault();
  }
};

const invalidPersonNamePat = /^[\x00-\x7F]+$/;

const ContentInput: React.FC = () => (
  <div>
    <div className='w-full items-center md:flex'>
      <div className='block w-full text-xl text-gray md:w-1/3'>
        <label htmlFor='grid-description'>発注内容概要</label>
        <div className='text-base md:pl-6'>
          ■必要な機能・課題
          <br className='hidden md:block' />
          ■ご予算
          <br className='hidden md:block' />
          ■スケジュールなど
          <br className='hidden md:block' />
        </div>
      </div>
      <div className='py-3 md:w-2/3 md:pb-0'>
        <textarea
          rows={6}
          name='content'
          maxLength={10240}
          className='w-full rounded border border-gray-ccc bg-white px-4  py-3 text-gray placeholder:text-base focus:bg-white focus:outline-none'
          id='form-description'
          placeholder={`※ご記入例：
      ■発注内容　病院予約システムの開発
      ■参考サイト　病院空き時間検索.com
      ■要望・状況　当院の予約システムを構築したい。ご利用頂いた方にアンケートを取るシステムも作りたい。
      ■予算　200～300万円目安
      ■納期　半年以内に納品希望`}
        />
      </div>
    </div>
  </div>
);

const FileInput: React.FC = () => (
  <div>
    <div className='w-full items-center md:flex'>
      <div className='block w-full text-xl text-gray md:w-1/3'>
        <label htmlFor='form-file'>添付書類</label>
        <div className='pb-2 text-base md:pl-6'>
          ■企画書やRFPの添付が可能です
          <br className='hidden md:block' />
          （10MBまで）
        </div>
      </div>
      <input
        onChange={(ev) => {
          if (
            Array.from(ev.target.files || []).reduce(
              (sum, f) => sum + f.size,
              0
            ) >
            1024 * 1024 * 10
          ) {
            alert('10MBを超えています');
            ev.target.value = '';
          }
        }}
        className='w-2/3 min-w-fit cursor-pointer text-base file:rounded file:border-none file:bg-[#ccc] file:px-5 file:py-2 file:text-base'
        id='form-file'
        type='file'
        name='files[]'
        multiple
      />
    </div>
  </div>
);

const SubmitArea: React.FC<{ disabled: boolean }> = ({ disabled }) => (
  <div className='text-center'>
    <p className='text-base'>
      会員登録には、
      <br className='sm:hidden' />
      発注ナビ&nbsp;
      <a
        href={tosPath}
        className='text-secondary hover:text-[#0095b6]'
        target='_blank'
        rel='noopener noreferrer'
      >
        利用規約
      </a>
      &nbsp; 及び&nbsp;
      <a
        href={privacyPolicyPath}
        className='text-secondary hover:text-[#0095b6]'
        target='_blank'
        rel='noopener noreferrer'
      >
        個人情報の取扱い
      </a>
      &nbsp;
      <br className='sm:hidden' />
      「当社からのメール受信」への同意が必要です。
    </p>
    <button
      disabled={disabled}
      className='mt-6 h-[60px] w-[300px] bg-top-primary px-6 py-3 text-xl text-white hover:bg-[#26c4e7] disabled:cursor-not-allowed disabled:bg-gray disabled:opacity-50'
    >
      上記に同意して、送信する
    </button>
  </div>
);

const CollapsedInput: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <div
    className='my-4 flex w-full cursor-pointer items-center rounded border border-gray-ccc bg-white p-8 text-gray hover:opacity-80'
    onClick={onClick}
  >
    <div className='pr-4'>
      <div className='text-xl'>発注内容の概要・添付資料</div>
      <div>※ご記入いただきますとその後の流れがスムーズに進みます</div>
    </div>
    <LinkBracket className='ml-auto rotate-90' />
  </div>
);

const CollapseButton: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <div className='flex'>
    <div
      className='ml-auto inline-block cursor-pointer rounded border border-gray-ccc bg-white p-4 text-gray hover:opacity-80'
      onClick={onClick}
    >
      <LinkBracket className='ml-auto -rotate-90' />
    </div>
  </div>
);

export const ContactForm: React.FC<{
  category?: string;
  hideContentInput?: boolean;
  hideFileInput?: boolean;
  formPostPath?: string;
  onSubmit?: React.ComponentPropsWithoutRef<'form'>['onSubmit'];
  collapsable?: boolean;
}> = ({
  category,
  hideContentInput,
  hideFileInput,
  formPostPath,
  onSubmit,
  collapsable,
}) => {
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [disabled, setDisabled] = useState(true);
  const [personNameError, setPersonNameError] = useState<string>();
  const [collapsed, setCollapsed] = useState(collapsable || false);

  useEffect(() => {
    const tmpPersonNameError =
      invalidPersonNamePat.test(firstName) ||
      invalidPersonNamePat.test(lastName)
        ? 'この項目は半角英数字のみのご入力ができません。全角文字でご入力をお願いします。'
        : firstName === '名'
        ? '「名」以外の文字を入力してください'
        : lastName === '姓'
        ? '「姓」以外の文字を入力してください'
        : undefined;
    setPersonNameError(tmpPersonNameError);
    if (
      !companyName ||
      !firstName ||
      !lastName ||
      !email ||
      !phoneNumber ||
      tmpPersonNameError
    ) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [companyName, firstName, lastName, email, phoneNumber]);

  const stateChange = (setstate: any /* TODO */) => (e: any /* TODO */) => {
    setstate(e.target.value);
  };

  return (
    <>
      <form
        action={formPostPath}
        method='post'
        encType='multipart/form-data'
        onSubmit={onSubmit}
      >
        <input type='hidden' name='category' value={category} />
        <input type='hidden' name='form_category' value='ラウンジ' />
        <div className='mb-6'>
          <div className='w-full items-center md:flex'>
            <div className='block flex w-full text-xl text-gray md:w-1/3'>
              <label htmlFor='form-company-name'>貴社名</label>
              <div className='ml-2 self-end text-base text-red'>必須</div>
            </div>
            <div className='py-3 md:w-2/3'>
              <input
                onChange={stateChange(setCompanyName)}
                className='h-14 w-full rounded border border-gray-ccc bg-white px-3 py-2 text-base leading-snug text-gray placeholder:text-base focus:bg-white focus:outline-none'
                name='company_name'
                id='form-company-name'
                type='text'
                onKeyDown={prevenetDefaultOnEnter}
                placeholder='（例）発注ナビ株式会社'
              />
            </div>
          </div>
          <div className='w-full items-center md:flex'>
            <div className='block flex w-full text-xl text-gray md:w-1/3'>
              <label htmlFor='form-family-name'>お名前</label>
              <div className='ml-2 self-end text-base text-red'>必須</div>
            </div>
            <div className='flex flex-wrap py-3 md:w-2/3'>
              <div className='flex w-full'>
                <input
                  onChange={stateChange(setLastName)}
                  className='mr-2 block w-full appearance-none rounded border border-gray-ccc bg-white px-4 py-3 text-gray placeholder:text-base focus:bg-white  focus:outline-none md:mb-0'
                  name='family_name'
                  id='form-family-name'
                  type='text'
                  onKeyDown={prevenetDefaultOnEnter}
                  placeholder='姓'
                />
                <input
                  onChange={stateChange(setFirstName)}
                  className='block w-full appearance-none rounded border border-gray-ccc bg-white px-4 py-3 text-gray placeholder:text-base focus:bg-white focus:outline-none md:ml-4'
                  name='given_name'
                  type='text'
                  placeholder='名'
                  onKeyDown={prevenetDefaultOnEnter}
                />
              </div>
              {personNameError && (
                <div className='mt-1 text-red'>{personNameError}</div>
              )}
            </div>
          </div>

          <div className='w-full items-center md:flex'>
            <div className='block flex w-full text-xl text-gray md:w-1/3'>
              <label htmlFor='form-department'>部署名</label>
            </div>
            <div className='py-3 md:w-2/3'>
              <input
                onChange={stateChange(setEmail)}
                className='w-full rounded border border-gray-ccc bg-white px-4 py-3 py-3 text-gray placeholder:text-base focus:bg-white focus:outline-none'
                id='form-department'
                name='department'
                placeholder='（例）情報システム部・総務部・営業部など'
                onKeyDown={prevenetDefaultOnEnter}
              />
            </div>
          </div>

          <div className='w-full items-center md:flex'>
            <div className='block flex w-full text-xl text-gray md:w-1/3'>
              <label htmlFor='form-email'>勤務先メールアドレス</label>
              <div className='ml-2 self-end text-base text-red'>必須</div>
            </div>
            <div className='py-3 md:w-2/3'>
              <input
                onChange={stateChange(setEmail)}
                className='w-full rounded border border-gray-ccc bg-white px-4  py-3 text-gray placeholder:text-base focus:bg-white focus:outline-none'
                id='form-email'
                name='email'
                type='email'
                onKeyDown={prevenetDefaultOnEnter}
                placeholder='（例）info@hnavi.co.jp'
              />
            </div>
          </div>

          <div className='w-full items-center md:flex'>
            <div className='block flex w-full text-xl text-gray md:w-1/3'>
              <label htmlFor='form-telephone'>
                日中につながりやすい
                <br className='hidden md:block xl:hidden' />
                <span className='whitespace-nowrap'>電話番号</span>
                <br className='hidden md:block' />
                <span className='ml-2 self-end text-base text-red md:ml-0'>
                  必須
                </span>
              </label>
            </div>
            <div className='py-3 md:w-2/3'>
              <input
                onChange={stateChange(setPhoneNumber)}
                className='w-full rounded border border-gray-ccc bg-white px-4 py-3  py-3 text-gray placeholder:text-base focus:bg-white focus:outline-none'
                id='form-telephone'
                name='telephone'
                type='tel'
                placeholder='（例）03-1234-5678'
                onKeyDown={prevenetDefaultOnEnter}
              />
            </div>
          </div>
          {collapsed && collapsable && (
            <CollapsedInput
              onClick={() => {
                setCollapsed((s) => !s);
              }}
            />
          )}
          <div className={collapsed ? 'hidden' : ''}>
            {!hideContentInput && <ContentInput />}
            {!hideFileInput && <FileInput />}
            {collapsable && (
              <CollapseButton
                onClick={() => {
                  setCollapsed((s) => !s);
                }}
              />
            )}
          </div>
        </div>
        <SubmitArea disabled={disabled} />
      </form>
    </>
  );
};

export default ContactForm;
